import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api';
import { useAppData } from '../../contexts/AppDataContext';
import { ChatMessage as ChatMessageType } from '../../types/chat';
import { LEAD_TABLE_STATUS } from '../../types';
import { createUserMessage, createAssistantMessage } from '../../utils/chatUtils';

interface TableContext {
  status?: string;
  criteria?: string[];
  description?: string;
  userPrompt?: string;
  type?: 'companies' | 'people' | 'custom';
}

interface UseEditableCriteriaProps {
  tableId?: string;
  tableContext?: TableContext;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>;
}

interface UseEditableCriteriaReturn {
  editableCriteria: string[];
  editingCriterionIndex: number | null;
  editingCriterionValue: string;
  hasEditedCriteria: boolean;
  isCreatingEditedSearch: boolean;
  editInputRef: React.RefObject<HTMLTextAreaElement>;
  handleEditCriterion: (index: number) => void;
  handleSaveCriterionEdit: () => void;
  handleCancelCriterionEdit: () => void;
  handleRemoveCriterion: (index: number) => void;
  handleRunEditedSearch: () => Promise<void>;
  setEditingCriterionValue: (value: string) => void;
}

/**
 * Hook for managing editable search criteria in no-results scenarios
 */
export function useEditableCriteria({
  tableId,
  tableContext,
  setMessages,
}: UseEditableCriteriaProps): UseEditableCriteriaReturn {
  const navigate = useNavigate();
  const { refreshDatasets } = useAppData();

  const [editableCriteria, setEditableCriteria] = useState<string[]>([]);
  const [editingCriterionIndex, setEditingCriterionIndex] = useState<number | null>(null);
  const [editingCriterionValue, setEditingCriterionValue] = useState('');
  const [hasEditedCriteria, setHasEditedCriteria] = useState(false);
  const [isCreatingEditedSearch, setIsCreatingEditedSearch] = useState(false);
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  // Initialize editable criteria when status becomes no-results
  useEffect(() => {
    if (tableContext?.status === LEAD_TABLE_STATUS.COMPLETED_NO_RESULTS && tableContext?.criteria) {
      setEditableCriteria(tableContext.criteria);
      setHasEditedCriteria(false);
    }
  }, [tableContext?.status, tableContext?.criteria]);

  // Focus edit input when editing starts
  useEffect(() => {
    if (editingCriterionIndex !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCriterionIndex]);

  const handleEditCriterion = useCallback((index: number) => {
    setEditingCriterionIndex(index);
    setEditingCriterionValue(editableCriteria[index]);
  }, [editableCriteria]);

  const handleSaveCriterionEdit = useCallback(() => {
    if (editingCriterionIndex !== null && editingCriterionValue.trim()) {
      const updated = [...editableCriteria];
      if (updated[editingCriterionIndex] !== editingCriterionValue.trim()) {
        setHasEditedCriteria(true);
      }
      updated[editingCriterionIndex] = editingCriterionValue.trim();
      setEditableCriteria(updated);
    }
    setEditingCriterionIndex(null);
    setEditingCriterionValue('');
  }, [editingCriterionIndex, editingCriterionValue, editableCriteria]);

  const handleCancelCriterionEdit = useCallback(() => {
    setEditingCriterionIndex(null);
    setEditingCriterionValue('');
  }, []);

  const handleRemoveCriterion = useCallback((index: number) => {
    setEditableCriteria(prev => prev.filter((_, i) => i !== index));
    setHasEditedCriteria(true);
  }, []);

  const handleRunEditedSearch = useCallback(async () => {
    if (!tableId || editableCriteria.length === 0 || isCreatingEditedSearch) return;

    setIsCreatingEditedSearch(true);

    // Add user message
    const userMessage = createUserMessage(
      hasEditedCriteria ? 'Run search with edited criteria' : 'Retry search',
      'user-edit'
    );
    setMessages(prev => [...prev, userMessage]);

    // Add assistant message showing progress
    const loadingMessage = createAssistantMessage(
      'Creating a new dataset with your criteria...',
      { idPrefix: 'assistant-edit' }
    );
    setMessages(prev => [...prev, loadingMessage]);

    try {
      // Get the original search query from context
      const searchQuery = tableContext?.description || tableContext?.userPrompt || '';
      const tableType = tableContext?.type || 'companies';

      // Create new dataset with edited criteria
      const response = await apiClient.generateEnhancedLeadTable(
        searchQuery,
        editableCriteria,
        tableType,
        10, // Default row count
        undefined, // customTypeDescription
        null, // uploadId
        null, // title
      );

      if (response.table_id) {
        // Refresh datasets cache
        await refreshDatasets();

        // Build chat messages for the new dataset with criteria array for pills display
        const chatMessages = [
          { role: 'user' as const, content: searchQuery, timestamp: new Date() },
          { role: 'assistant' as const, content: `Got it! Here's what I'll search for:`, timestamp: new Date(), criteria: editableCriteria }
        ];

        // Navigate to new dataset
        navigate(`/datasets/${response.table_id}`, {
          state: { initialChatMessages: chatMessages }
        });
      } else {
        // Error - update the loading message
        setMessages(prev => prev.map(m =>
          m.id === loadingMessage.id
            ? { ...m, content: 'Failed to create dataset. Please try again.' }
            : m
        ));
      }
    } catch (error) {
      console.error('Error creating dataset with edited criteria:', error);
      // Update the loading message with error
      setMessages(prev => prev.map(m =>
        m.id === loadingMessage.id
          ? { ...m, content: 'Failed to create dataset. Please try again.' }
          : m
      ));
    } finally {
      setIsCreatingEditedSearch(false);
    }
  }, [tableId, editableCriteria, isCreatingEditedSearch, hasEditedCriteria, tableContext, setMessages, refreshDatasets, navigate]);

  return {
    editableCriteria,
    editingCriterionIndex,
    editingCriterionValue,
    hasEditedCriteria,
    isCreatingEditedSearch,
    editInputRef,
    handleEditCriterion,
    handleSaveCriterionEdit,
    handleCancelCriterionEdit,
    handleRemoveCriterion,
    handleRunEditedSearch,
    setEditingCriterionValue,
  };
}
